import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppointmentService, IAppointment } from '@shared/services/appointment.service';
import { IWorktask } from '@shared/services/worktask.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';
import { Subject } from 'rxjs';

export interface IDialogData {
	appointment: IAppointment | null;
	workTasks: IWorktask[];
}

const emptyGuid = '00000000-0000-0000-0000-000000000000';

@Component({
	selector: 'app-create-appointment',
	templateUrl: 'create-appointment.html',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class CreateAppointmentComponent implements OnDestroy {
	@Output() created: EventEmitter<void> = new EventEmitter();
	private destroyNotifier$ = new Subject<void>();
	public form: FormGroup;
	public loading: boolean = false;
	public httpError: string = '';
	public appointment: IAppointment;
	public workTasks: IWorktask[] = [];
	public appointmentTypes = this.appointmentService.getAppointmentTypes();
	public range: FormGroup;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: IDialogData,
		public dialogRef: MatDialogRef<CreateAppointmentComponent>,
		private appointmentService: AppointmentService,
		private fb: FormBuilder,
	) {
		this.appointment = { ...data.appointment };
		this.workTasks = this.data.workTasks.filter((workTask) => workTask.isActive && workTask.id && workTask.id !== emptyGuid).sort((a, b) => a.alias.toLowerCase().localeCompare(b.alias.toLowerCase()));
	}

	public ngOnInit(): void {
		this.initializeForm();
	}

	public ngOnDestroy(): void {
		this.destroyNotifier$.next();
		this.destroyNotifier$.complete();
	}

	private initializeForm(): void {
		this.range = this.fb.group({
			startTime: new FormControl(this.appointment.startTime, [Validators.required]),
			endTime: new FormControl(this.appointment.endTime, [Validators.required]),
		});

		this.form = this.fb.group({
			title: new FormControl(this.appointment.title, [Validators.required, Validators.pattern(ValidatorPatterns.Alias)]),
			workTaskId: new FormControl(this.appointment.workTaskId),
			appointmentType: new FormControl(this.appointment.appointmentType, [Validators.required]),
		});

		this.subscribeToFormChanges();
	}

	private subscribeToFormChanges(): void {
		this.form.valueChanges.subscribe((value) => {
			this.appointment = { ...this.appointment, ...value };
		});
	}

	public async onSave() {
		this.httpError = '';
		if (this.form.invalid || this.range.invalid) return;

		this.appointment.startTime = this.range.get('startTime').value;
		this.appointment.endTime = this.range.get('endTime').value;

		this.appointmentService.createAppointment(this.appointment).subscribe({
			next: () => {
				this.created.emit();
				this.dialogRef.close();
			},
			error: (errorResponse) => {
				this.httpError = `${errorResponse.status}`;
			},
		});
	}

	public onCancel(): void {
		this.dialogRef.close();
	}

	public getFirstValidationErrorName(formControlName: string) {
		const errors = this.form.controls[formControlName]?.errors;
		return errors ? Object.keys(errors)[0] : null;
	}
}
